import { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { DEFAULT_KEY_CONFIG, KeyConfig, KeyMapItem, TabType } from "../@types/keyboard";
import { getKeyDisplay, parseKeyConfig, serializeKeyConfig } from "../utils/keyboardUtils";
import { editProjectConfig } from "../services/projectService";

export function useKeyMapping(initialConfigStr?: string) {
  const { id } = useParams();

  const [config, setConfig] = useState<KeyConfig>(() => {
    if (initialConfigStr) return parseKeyConfig(initialConfigStr);
    console.log("initialConfigStr", initialConfigStr, parseKeyConfig(initialConfigStr!!));
    return JSON.parse(JSON.stringify(DEFAULT_KEY_CONFIG));
  });

  const [hasChanges, setHasChanges] = useState(false);
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;

  useEffect(() => {
    if (!initialConfigStr) return;
    if (hasChangesRef.current) return;
    setConfig(parseKeyConfig(initialConfigStr));
  }, [initialConfigStr]);

  const updateKey = useCallback((itemId: string, e: KeyboardEvent) => {
    if (e.key === "Escape") return;

    const specialMap: Record<string, string> = {
      ArrowUp: "↑",
      ArrowDown: "↓",
      ArrowLeft: "←",
      ArrowRight: "→",
      Backspace: "⌫",
      Enter: "↵",
      " ": "Space",
      Escape: "Esc",
    };
    const keyLabel = specialMap[e.key] ?? (e.key.length === 1 ? e.key.toUpperCase() : e.key);

    const modifier: "Ctrl" | "Alt" | undefined =
      e.ctrlKey && e.key !== "Control" ? "Ctrl" : e.altKey && e.key !== "Alt" ? "Alt" : undefined;

    setConfig((prev) => {
      const next: KeyConfig = JSON.parse(JSON.stringify(prev));
      for (const tab of Object.keys(next) as TabType[]) {
        const item = next[tab].find((i) => i.id === itemId);
        if (item) {
          item.key = e.key;
          item.display = keyLabel;
          if (modifier) item.modifier = modifier;
          else delete item.modifier;
          break;
        }
      }
      return next;
    });

    setHasChanges(true);
  }, []);

  const resetItem = useCallback((itemId: string) => {
    setConfig((prev) => {
      const next: KeyConfig = JSON.parse(JSON.stringify(prev));
      for (const tab of Object.keys(next) as TabType[]) {
        const item = next[tab].find((i) => i.id === itemId);
        if (item) {
          item.key = item.default;
          item.display = item.defaultDisplay;
          const ctrlDefaults: Record<string, "Ctrl"> = { a1: "Ctrl", a2: "Ctrl", a3: "Ctrl" };
          if (ctrlDefaults[itemId]) item.modifier = ctrlDefaults[itemId];
          else delete item.modifier;
          break;
        }
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  const resetAll = useCallback(() => {
    setConfig(JSON.parse(JSON.stringify(DEFAULT_KEY_CONFIG)));
    setHasChanges(true);
  }, []);

  const save = useCallback(async () => {
    const partial = JSON.parse(serializeKeyConfig(config)); // { keyConfig: { ... } }
    await editProjectConfig(Number(id), partial);
    setHasChanges(false);
  }, [config, id]);

  const findConflict = useCallback(
    (itemId: string, key: string, modifier?: "Ctrl" | "Alt") => {
      for (const items of Object.values(config)) {
        const found = (items as KeyMapItem[]).find(
          (i) => i.id !== itemId && i.key === key && (i.modifier ?? undefined) === modifier,
        );
        if (found) return found;
      }
      return undefined;
    },
    [config],
  );

  const getItemConflict = useCallback(
    (itemId: string, key: string, modifier?: "Ctrl" | "Alt") => findConflict(itemId, key, modifier),
    [findConflict],
  );

  const getTabConflicts = useCallback(
    (tab: TabType) => config[tab].filter((i) => findConflict(i.id, i.key, i.modifier)),
    [config, findConflict],
  );

  return {
    config,
    hasChanges,
    updateKey,
    resetItem,
    resetAll,
    save,
    getItemConflict,
    getTabConflicts,
    getKeyDisplay,
  };
}
